import { useState, useEffect, useRef } from "react";
import "./controle.css";
import home from "../../assets/icons/bookfuncionario/turn.png";
import { useNavigate } from "react-router-dom";
import { db } from "../../services/firebaseConnection";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

const Controle = () => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState({
    id: "",
    name: "FUNCIONÁRIO",
    role: "FUNÇÃO",
    cpf: "CPF",
    project: "OBRA",
    situation: "",
  });
  const [trainingsList, setTrainingsList] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const normalizeText = (str) => {
    if (!str) return "";

    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .trim();
  };

  const formatCPF = (cpf) => {
    if (!cpf || cpf === "CPF") return "CPF";

    const numbers = cpf.replace(/\D/g, "");

    if (numbers.length !== 11) return "CPF";

    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const cleanCPF = (cpf) => {
    return cpf?.replace(/\D/g, "") || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--/--/----";

    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split("-");
        return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
      }

      const date = new Date(dateString);

      if (isNaN(date.getTime())) return "--/--/----";

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return "--/--/----";
    }
  };

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;

    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split("-");
        return new Date(Number(year), Number(month) - 1, Number(day));
      }

      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const calculateExpirationDate = (dateString, months) => {
    if (!dateString || !months) return null;

    try {
      const baseDate = parseLocalDate(dateString);

      if (!baseDate) return null;

      const expirationDate = new Date(baseDate);
      expirationDate.setMonth(expirationDate.getMonth() + parseInt(months, 10));

      return expirationDate;
    } catch {
      return null;
    }
  };

  const calculateExpiration = (dateString, months, possuiVencimento = true) => {
    if (!possuiVencimento || !months) return "SEM VENCIMENTO";

    const expirationDate = calculateExpirationDate(dateString, months);

    if (!expirationDate) return "--/--/----";

    return formatDate(expirationDate);
  };

  const getTrainingStatus = (expirationDate) => {
    if (!expirationDate) return "status-ok";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(expirationDate);
    compareDate.setHours(0, 0, 0, 0);

    const diffTime = compareDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "status-expired";
    if (diffDays <= 30) return "status-warning";
    return "status-ok";
  };

  const getStatusPriority = (status) => {
    switch (status) {
      case "status-expired":
        return 0;
      case "status-warning":
        return 1;
      case "status-ok":
      default:
        return 2;
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const querySnapshot = await getDocs(
          query(collection(db, "funcionarios"), orderBy("nome")),
        );

        const employees = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            searchName: normalizeText(doc.data().nome || ""),
            cleanCpf: cleanCPF(doc.id),
          }))
          .filter((employee) => employee.situacao === "ATIVO");

        setAllEmployees(employees);
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Erro ao buscar funcionários:", error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchEmployees = (searchTerm) => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const normalizedTerm = normalizeText(searchTerm);
    const cpfOnly = cleanCPF(searchTerm);

    const results = allEmployees.filter((emp) => {
      const startsWithName = emp.searchName.includes(normalizedTerm);
      const exactCpfMatch = emp.cleanCpf === cpfOnly;
      const partialCpfMatch = emp.cleanCpf.includes(cpfOnly);
      const isLikelyCpf = /^\d+$/.test(searchTerm);

      if (isLikelyCpf) {
        return (
          (cpfOnly.length >= 3 && partialCpfMatch) ||
          (cpfOnly.length === 11 && exactCpfMatch)
        );
      }

      return startsWithName;
    });

    setSuggestions(results.slice(0, 5));
    setShowSuggestions(results.length > 0);
  };

  const handleEmployeeSelect = async (employee) => {
    setSearchInput(employee.nome);

    setSelectedEmployee({
      id: employee.id,
      name: employee.nome,
      role: employee.funcao || "NÃO INFORMADO",
      cpf: formatCPF(employee.id),
      project: employee.obra || "NÃO INFORMADO",
      situation: employee.situacao || "",
    });

    setShowSuggestions(false);

    try {
      const trainingsRef = collection(
        db,
        "funcionarios",
        employee.id,
        "treinamentos",
      );

      const querySnapshot = await getDocs(trainingsRef);

      if (!querySnapshot.empty) {
        const trainings = querySnapshot.docs.map((doc) => {
          const data = doc.data();

          const expirationDate = calculateExpirationDate(
            data.data,
            data.validadeMeses,
          );

          const trainingStatus = getTrainingStatus(expirationDate);

          return {
            ...data,
            formattedDate: formatDate(data.data),
            formattedExpiration: calculateExpiration(
              data.data,
              data.validadeMeses,
              data.possuiVencimento,
            ),
            expirationDate,
            trainingStatus,
          };
        });

        trainings.sort((a, b) => {
          const priorityDiff =
            getStatusPriority(a.trainingStatus) -
            getStatusPriority(b.trainingStatus);

          if (priorityDiff !== 0) return priorityDiff;

          const timeA = a.expirationDate
            ? a.expirationDate.getTime()
            : Infinity;
          const timeB = b.expirationDate
            ? b.expirationDate.getTime()
            : Infinity;

          return timeA - timeB;
        });

        setTrainingsList(trainings);
      } else {
        setTrainingsList([]);
      }
    } catch (error) {
      console.error("Erro ao buscar treinamentos:", error);
      setTrainingsList([]);
    }
  };

  const returnHome = () => {
    navigate("/main");
  };

  return (
    <div className="container-controle">
      <div className="controle-global">
        <div className="search-controle" ref={suggestionsRef}>
          <input
            ref={inputRef}
            type="text"
            placeholder="CPF ou NOME"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value.toUpperCase());
              searchEmployees(e.target.value);
            }}
            onFocus={() => {
              if (!isInitialLoad && searchInput) {
                searchEmployees(searchInput);
              }
            }}
            onKeyDown={(e) => e.key === "Enter" && searchEmployees(searchInput)}
            autoFocus
          />

          {showSuggestions && (
            <ul className="sugestoes-lista">
              {suggestions.length > 0 ? (
                suggestions.map((emp) => (
                  <li
                    key={emp.id}
                    className="sugestao-item"
                    onClick={() => handleEmployeeSelect(emp)}
                  >
                    {emp.nome} — {formatCPF(emp.id)}
                  </li>
                ))
              ) : (
                <li className="sugestao-item">Nenhum resultado encontrado</li>
              )}
            </ul>
          )}
        </div>

        <div className="dados-cadastro-controle">
          <div className="dados-primarios-controle">
            <p style={{ width: "80%" }}>{selectedEmployee.name}</p>
            <p style={{ width: "20%" }}>{selectedEmployee.project}</p>
          </div>

          <div className="dados-secundarios-controle">
            <p>{selectedEmployee.role}</p>
            <p>{selectedEmployee.cpf}</p>
          </div>
        </div>

        <div className="dados-info-container">
          <div className="cabecalho-controle">
            <p style={{ width: "28%" }}>TREINAMENTO</p>
            <p style={{ width: "20%" }}>USUÁRIO QUE LANÇOU</p>
            <p style={{ width: "16%" }}>INSTRUTOR</p>
            <p style={{ width: "18%" }}>DATA DE LANÇAMENTO</p>
            <p style={{ width: "18%" }}>VENCIMENTO</p>
          </div>

          <div className="dados-info-scroll">
            {trainingsList.length > 0 ? (
              trainingsList.map((training, index) => (
                <div
                  key={index}
                  className={`info-controle ${training.trainingStatus}`}
                >
                  <p style={{ width: "28%" }}>
                    {training.treinamentoNome ||
                      training.tipo ||
                      "NÃO INFORMADO"}
                  </p>

                  <p style={{ width: "20%" }}>
                    {training.cadastradoPor || "NÃO INFORMADO"}
                  </p>

                  <p style={{ width: "16%" }}>
                    {training.avaliador?.toUpperCase() || "NÃO INFORMADO"}
                  </p>

                  <p style={{ width: "18%" }}>{training.formattedDate}</p>

                  <p style={{ width: "18%" }}>{training.formattedExpiration}</p>
                </div>
              ))
            ) : (
              <div className="info-controle">
                <p style={{ width: "28%" }}>NENHUM TREINAMENTO</p>
                <p style={{ width: "20%" }}>--</p>
                <p style={{ width: "16%" }}>--</p>
                <p style={{ width: "18%" }}>--/--/----</p>
                <p style={{ width: "18%" }}>--/--/----</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="footer-controle">
        <img
          className="icon-home-controle"
          onClick={returnHome}
          src={home}
          alt="icon-home"
        />
      </div>
    </div>
  );
};

export default Controle;
